<template>
	<div>
		<l-map
			:center="center"
			:fade-animation="true"
			:options="mapOptions"
			:zoom-animation="true"
			:zoom.sync="zoom"
			no-blocking-animations
			style="z-index: 0"
		>
			<l-tile-layer
				:url="url"
				:attribution="attribution"
			/>
			<l-marker
				v-for="psy in psychologists"
				:key="psy.idByAddress"
				:icon="coordinates === psy.getCoordinatesByAddress().reverse().join(';') ? redIcon : blueIcon"
				:lat-lng="psy.getCoordinatesByAddress()"
				@click="handleMarkerClick(psy)"
			/>
			<l-control-scale
				position="topright"
				:imperial="false"
				:metric="true"
			/>
		</l-map>
	</div>
</template>

<script lang="ts">
	import Vue from 'vue';
	import { icon, Icon } from 'leaflet';
	import { LMap, LMarker, LTileLayer, LTooltip, LControlScale } from 'vue2-leaflet';
	import Component from 'vue-class-component';
	import { Prop, Watch } from 'vue-property-decorator';

	import { SearchPsychologist } from '@/types/models/SearchPsychologist';
	import { City } from '@/types/models/City';
	import { INITIAL_ZOOM_MAP } from '@/constants';
	import { PsychologistService } from '@/services/psychologist-service';
	import { MapConfigUtil } from '@/utils/map-config-util';

	/**
	 * Map des psychologues
	 */
	@Component({
		components: {
			LMap,
			LTileLayer,
			LMarker,
			LTooltip,
			LControlScale
		}
	})
	export default class PsychologistMap extends Vue {
		@Prop({ default: () => [] })
		readonly psychologists!: SearchPsychologist[];

		@Prop()
		readonly city!: City;

		// ID du psychologue composé de son id et de ses coordonnées x, y
		@Prop({ default: null })
		readonly psychologistIdByAddress!: string | null;

		selectedId: string | null = null;

		// Propriétés pour la map
		coordinates: string | null = null;
		zoom = INITIAL_ZOOM_MAP;
		icon = icon;
		readonly mapWindow = window as unknown as Window & {
			URL_MAP: string;
			URL_MAP_SERVICE: string;
			URL_MAP_REQUEST: string;
			URL_MAP_VERSION: string;
			URL_MAP_LAYER: string;
			ATTRIBUTION_MAP: string;
		};
		readonly url = MapConfigUtil.resolveMapUrl(
			this.mapWindow.URL_MAP,
			this.mapWindow.URL_MAP_SERVICE,
			this.mapWindow.URL_MAP_REQUEST,
			this.mapWindow.URL_MAP_VERSION,
			this.mapWindow.URL_MAP_LAYER
		);
		readonly attribution = MapConfigUtil.resolveAttribution(this.mapWindow.ATTRIBUTION_MAP);
		readonly mapOptions = {
			zoomSnap: 0.5
		};
		// marker par défaut
		readonly blueIcon = this.getMarkerIcon('./img/marker-icon-2x-blue.png');
		// marker lorsqu'un psychologue est sélectionné
		readonly redIcon = this.getMarkerIcon('./img/marker-icon-2x-red.png');

		mounted() {
			this.zoom = this.getInitialZoom();
		}

		/**
		 * Centre la Map en fonction du psychologue sélectionné, sinon centre sur la ville
		 */
		get center(): number[] {
			if (this.selectedId) {
				return PsychologistService.getCoordinatesFromPsychologistIdByAddress(this.selectedId).split(';').reverse().map(c => parseFloat(c));
			} else {
				return [this.city.coordinates_y, this.city.coordinates_x];
			}
		}

		/**
		 * Initialise le zoom, les coordonnées et le psychologue selectionné lorsqu'un nouveau psychologue est récupéré
		 */
		@Watch('psychologistIdByAddress')
		handlePsychologistIdChange(): void {
			const ZOOM_VALUE_14 = 14;

			if (this.psychologistIdByAddress) {
				this.zoom = ZOOM_VALUE_14;
				this.selectedId = this.psychologistIdByAddress;
				this.coordinates = PsychologistService.getCoordinatesFromPsychologistIdByAddress(this.psychologistIdByAddress);
			}
		}

		/**
		 * Réinitialise le psychologue selectionné ainsi que le zoom à chaque nouvelle recherche
		 */
		@Watch('psychologists')
		handleChangePsychologistList(): void {
			this.zoom = this.getInitialZoom();
			this.selectedId = null;
		}

		/**
		 * Initialise les coordonnées et envoie le psychologue selectionné au parent lors du clic sur le marker
		 * @param selectedPsychologist le psychologue selectionné
		 */
		handleMarkerClick(selectedPsychologist: SearchPsychologist): void {
			this.coordinates = selectedPsychologist.getCoordinatesByAddress().join(';');
			this.$emit('on-change-psychologist', selectedPsychologist.idByAddress);
		}

		/**
		 * Retourne une valeur en fonction de la distance du premier psychologue trouvé
		 * @returns 12 par défaut
		 */
		getInitialZoom(): number {
			// constantes pour les distances
			const DISTANCE_MAX_50 = 50;
			const DISTANCE_MAX_30 = 30;
			const DISTANCE_MAX_10 = 10;
			const DISTANCE_MAX_0 = 0;

			// constantes pour les niveaux de zoom
			const ZOOM_VALUE_8 = 8;
			const ZOOM_VALUE_10 = 10;
			const ZOOM_VALUE_11 = 11;
			const DEFAULT_ZOOM_VALUE = 12;

			const distanceFirstPsychologist = this.psychologists[0].distance;

			let zoomValue = DEFAULT_ZOOM_VALUE;
			if (distanceFirstPsychologist) {
				switch (true) {
					case distanceFirstPsychologist > DISTANCE_MAX_50:
						zoomValue = ZOOM_VALUE_8;
						break;

					case distanceFirstPsychologist > DISTANCE_MAX_30:
						zoomValue = ZOOM_VALUE_10;
						break;

					case distanceFirstPsychologist > DISTANCE_MAX_10:
						zoomValue = ZOOM_VALUE_11;
						break;

					case distanceFirstPsychologist > DISTANCE_MAX_0:
						zoomValue = DEFAULT_ZOOM_VALUE;
						break;

					default:
						zoomValue = DEFAULT_ZOOM_VALUE;
				}
			}

			return zoomValue;
		}

		/**
		 * Défini les paramètres pour le marker
		 * @param path le chemin du png à utiliser pour l'icône
		 */
		getMarkerIcon(path: string): Icon {
			const ICON_SIZE_X = 25;
			const ICON_SIZE_Y = 41;
			const ICON_ANCHOR_X = 12;
			const ICON_ANCHOR_Y = 41;
			const POP_UP_ANCHOR_X = 1;
			const POP_UP_ANCHOR_Y = -34;
			const SHADOW_SIZE_X = 41;
			const SHADOW_SIZE_Y = 41;

			return new Icon({
				iconUrl:
					path,
				shadowUrl:
					'./img/marker-shadow.png',
				// propriétés du marker
				iconSize: [ICON_SIZE_X, ICON_SIZE_Y],
				iconAnchor: [ICON_ANCHOR_X, ICON_ANCHOR_Y],
				popupAnchor: [POP_UP_ANCHOR_X, POP_UP_ANCHOR_Y],
				shadowSize: [SHADOW_SIZE_X, SHADOW_SIZE_Y]
			});
		}
	}
</script>
